import { colors, defaultOpacity } from '../../constants/constants.js';
import { findFirstLevel } from '../../functions/finder.js';
import { DefaultDict } from '../defaultdict.js';
import { getBimElement } from '../getter.js';
import { _ } from '../translator.js';
import { getElementData, polygonsAreEqual } from '../utils.js'; 
import { categoriesHeights,
        defaultBaseHeight,
        defaultHeight } from './polygonHeights.js';


export const terrainExaggeration = 1

export const map3d = {
    type: "raster-dem",
    url: "mapbox://mapbox.mapbox-terrain-dem-v1",
    tileSize: 512
}

export const sky = {
    id: "sky",
    type: "sky",
    paint: {
        "sky-type": "atmosphere",
        "sky-atmosphere-sun": [90.0, 80.0],
        "sky-atmosphere-sun-intensity": 1
    }
}

export const projectLevelLayer = {
    id: 'projectLevel',
    source: 'projectLevel',
    type: 'raster'
}

export const cardPointLayer = {
    id: 'cardPointLayer',
    type: 'symbol',
    source: 'cards',
    layout: {
        'icon-image': 'cardMarker',
        'icon-allow-overlap': true,
        'icon-size': 1,
        'icon-anchor': 'bottom'
    },
    paint: {
        'icon-color': [
            'case',
            ['any', 
                ['boolean', ['feature-state', 'highlight'], false],
                ['boolean', ['feature-state', 'hover'], false]
            ],
            colors['onstageobra'],
            ['get', 'color']
        ],
        'icon-halo-color': 'black',
        'icon-halo-width': 3,
        'icon-opacity': defaultOpacity,
    },
    filter: ['==', '$type', 'Point']
}

export const cardLineStringLayer = {
    id: 'cardLineStringLayer',
    type: 'line',
    source: 'cards',
    layout: {
        'line-join': 'round',
        'line-cap': 'round'
    },
    paint: {
        'line-width': 7,
        'line-color': [
            'case',
            ['any', 
                ['boolean', ['feature-state', 'highlight'], false],
                ['boolean', ['feature-state', 'hover'], false]
            ],
            colors['onstageobra'],
            ['get', 'color']
        ],
        'line-opacity': defaultOpacity,
    },
    filter: ['==', '$type', 'LineString']
}

export const cardPolygonLayer = {
    id: 'cardPolygonLayer',
    type: 'fill-extrusion',
    source: 'cards',
    paint: {
        'fill-extrusion-color': [
            'case',
            ['any', 
                ['boolean', ['feature-state', 'highlight'], false],
                ['boolean', ['feature-state', 'hover'], false]
            ],
            colors['onstageobra'],
            ['get', 'color']
        ],
        'fill-extrusion-opacity': defaultOpacity,
        'fill-extrusion-base': ['number', ['get', 'base_height'], defaultBaseHeight],
        'fill-extrusion-height': ['number', ['get', 'height'], defaultHeight],
    },
    filter: ['==', '$type', 'Polygon']
}

export const cardOutlinePolygonLayer = {
    id: 'cardOutlinePolygonLayer',
    type: 'line',
    source: 'cards',
    paint: {
        'line-width': 2,
        'line-color': [
            'case',
            ['any', 
                ['boolean', ['feature-state', 'highlight'], false],
                ['boolean', ['feature-state', 'hover'], false]
            ],
            colors['onstageobra'],
            ['get', 'color']
        ],
        'line-opacity': defaultOpacity,
    },
    filter: ['==', '$type', 'Polygon']
}

export const cardNumbersLayer = {
    id: 'cardNumbersLayer',
    type: 'symbol',
    source: 'cards',
    layout: {
        'text-field': '{index}',
        'text-allow-overlap': true,
        'text-size': 12,
        'text-font': ['Arial Unicode MS Bold','Open Sans Bold'],
        'text-offset': [ 
            'case', 
            ['==', ['geometry-type'], 'Point'],
            ['literal', [0, -2.3]],
            ['literal', [0, 0]]
        ],
        'visibility': 'none'
    },
    paint: {
        'text-color': 'black',
    }
}

export const axlesLineLayer = {
    id: 'axlesLineLayer',
    type: 'line',
    source: 'axles',
    layout: {
        'line-cap': 'round',
        'line-join': 'round',
        'visibility': 'none'
    },
    paint: {
        'line-color': '#3c9ab2',
        'line-width': 2
    }
}

export const axleNamesLayer = {
    id: 'axleNamesLayer',
    type: 'symbol',
    source: 'axleNames',
    layout: {
        'text-field': '{name}',
        'text-anchor': 'bottom',
        'text-padding': 10,
        'text-size': 18,
        'text-font': ['Arial Unicode MS Bold','Open Sans Bold'],
        'visibility': 'none'
    },
    paint: {
        'text-color': 'white',
        'text-halo-color': colors.onstageobra,
        'text-halo-width': 20
    }
}

export function projectLevelSource (levelData) {
    return {
        type: 'image',
        url: levelData[0],
        coordinates: levelData[1]
    }
}

export function createGeoJSONSource (coordinates, geometryType) {
    return {
        type: 'geojson',
        data: {
            type: 'Feature',
            properties: {},
            geometry: {
                type: geometryType,
                coordinates: coordinates
            }
        }
    }
}

export function createFeatureCollection(features) {
    return {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features: features
        }
    }
}

export function createFeatureCollections(features, featuresToRender) {
    let featureCollections = []
    features.forEach(feature => {
        if(featuresToRender.includes(feature.id)) {
            let featureCollection = createFeatureCollection([feature])
            featureCollections.push(featureCollection)
        }
    })
    return featureCollections
}

function getGeometryType(gpsPoints) {
    switch(gpsPoints.length) {
        case 1:
            return 'Point'
        case 2:
        case 3:
            return 'LineString'
        default:
            return 'Polygon'
    }
}

function prepareCoordinates(coordinates, geometryType){
    switch(geometryType) {
        case 'Point':
            return coordinates[0]
        case 'LineString':
            return coordinates
        case 'Polygon':
            // Close the polygon.
            let lastIndex = coordinates.length -1
            if (coordinates[0][0] !== coordinates[lastIndex][0] || coordinates[0][1] !== coordinates[lastIndex][1]) {
                coordinates.push(coordinates[0])
            }
            return [coordinates]
        default:
            return coordinates
    }
}

export function createCardFeature (coordinates, cardsGroupData, properties) {
    const geometryType = getGeometryType(coordinates)
    const geojsonCoordinates = prepareCoordinates(coordinates, geometryType)
    return {
        id: cardsGroupData.id,
        type: 'Feature',
        geometry: {
            type: geometryType,
            coordinates: geojsonCoordinates
        },
        properties: properties
    }
}

export function getLevelData(projectData, level) {
    let imgPath = ''
    if (level !== "unset") {
        if (level === 'Todo') {
            const firstLevel = findFirstLevel(projectData)
            imgPath = `/images/${firstLevel.attributes.image_path}`
        } else {
            imgPath = `/images/${level.attributes.image_path}`
        }
    }
    let imgCoordinates = getImageCoordinates(projectData, level)
    let levelData = [imgPath, imgCoordinates]
    return levelData
}

function getImageCoordinates(projectData, level) {
    let imgCoordinates = []
    let coordinates = projectData.attributes.coordinates
    if (level !== 'Todo') {
        imgCoordinates = [
            [level.attributes.top_left_longitude, level.attributes.top_left_latitude],
            [level.attributes.top_right_longitude, level.attributes.top_right_latitude],
            [level.attributes.down_right_longitude, level.attributes.down_right_latitude],
            [level.attributes.down_left_longitude, level.attributes.down_left_latitude]	
        ]
    } else {
        imgCoordinates = [
            [coordinates.topLeft[0], coordinates.topLeft[1]],
            [coordinates.downRight[0], coordinates.topLeft[1]],
            [coordinates.downRight[0], coordinates.downRight[1]],
            [coordinates.topLeft[0], coordinates.downRight[1]]	
        ]
    }
    return imgCoordinates
}

function getGpsPointsGroup(gpsPoints) {
    let groups = {}
    Object.values(gpsPoints).forEach(point => {
        let groupId = point.attributes.group_index
        let coordinates = [point.attributes.longitude, point.attributes.latitude]
        if (groups[groupId]) {
            groups[groupId].push(coordinates)
        } else {
            groups[groupId] = [coordinates]
        }
    })
    return Object.values(groups)
}

function getFeatureHeight(heightType, levelData, categoryData, figureData) {
    const levelHeight = levelData.attributes.height;
    if (figureData.attributes.top_height) {
        const figureHeightType = heightType === 'height' ? 'top_height' : heightType;
        const figureHeight = figureData.attributes[figureHeightType];
        return figureHeight;
    }
    if (!categoryData) {
        return defaultHeight;
    }
    if (categoryData.attributes[heightType]) {
        const categoryHeight = categoryData.attributes[heightType];
        return levelHeight + categoryHeight;
    }
    let categoryName = categoryData.attributes.name.toLowerCase();
    for (const category of Object.keys(categoriesHeights)) {
        if (categoryName.includes(category)) {
            categoryName = category;
            break;
        }
    }
    let categoryHeights = categoriesHeights[categoryName];
    if (categoryHeights) {
        return levelHeight + categoryHeights[heightType];
    }
}

function groupFeatures(features) {
    let orderedFeatures = {}
    let fixedFeatures = []
    features.forEach(feature => {
        if (feature.geometry.type !== 'Polygon') {
            fixedFeatures.push(feature)
            return
        }
        let length = feature.geometry.coordinates[0].length.toString()
        if (orderedFeatures.hasOwnProperty(length)) {
            orderedFeatures[length] = groupFeaturesByCoordinates(feature, orderedFeatures[length])
        } else {
            orderedFeatures[length] = [[feature]]
        }
    })
    // console.log('orderedFeatures:', orderedFeatures)
    Object.values(orderedFeatures).forEach(featuresGroups => {
        featuresGroups.forEach(featuresGroup => {
            if (featuresGroup.length > 1) {
                // console.log('GROUP OF FEATURES FOUND: ', featuresGroup)
                fixedFeatures.push(mergeFeaturesGroup(featuresGroup))
            } else {
                fixedFeatures.push(featuresGroup[0])
            }
        })
    })
    return fixedFeatures
}

function groupFeaturesByCoordinates(feature, orderedFeatures) {
    for (let i = 0; i < orderedFeatures.length; i++) {
        const featureToCompare = orderedFeatures[i][0]
        let coordinates = feature.geometry.coordinates[0]
        let coordinatesToCompare = featureToCompare.geometry.coordinates[0]
        if (feature.properties.card_category_id === featureToCompare.properties.card_category_id
            && polygonsAreEqual(coordinates, coordinatesToCompare)) {
            orderedFeatures[i].push(feature)
            return orderedFeatures
        }
    }
    orderedFeatures.push([feature])
    return orderedFeatures
}

//DEPRECATED
export function createCardsFeatures(cardsGroupsData, projectData, stageId) {
    let cardFeatures = []
    const levelsData = projectData.levels;
    const cardCategoriesData = projectData.card_categories;
    const customcardsData = projectData.customcards;
    for (const cardsGroupData of Object.values(cardsGroupsData)) { 
        let properties = { 
            ...cardsGroupData.attributes 
        };
        properties.stage_id = stageId;
        properties.cards_group_ids = [cardsGroupData.id];
        let cardCategoryData = getElementData(cardCategoriesData, cardsGroupData.attributes.card_category_id);
        let levelData = getElementData(levelsData, cardsGroupData.attributes.level_id);
        let customcardData = getElementData(customcardsData, cardsGroupData.attributes.customcard_id);
        properties.color = cardCategoryData ? cardCategoryData.attributes.color : 'black';
        properties.custom_level = customcardData ? customcardData.attributes.level_id : _('Piso');
        properties.custom_category = customcardData ? customcardData.attributes.card_category_id : _('Categoría');
        properties.category_name = cardCategoryData ? cardCategoryData.attributes.name : '';
        properties.level_name = levelData ? levelData.attributes.name : '';
        // if (cardCategoryData) {
        //     properties.height = getFeatureHeight('height', cardCategoryData, levelData);
        //     properties.base_height = getFeatureHeight('base_height', cardCategoryData, levelData);
        // }
        let gpsPointsGroups = [];
        if (cardsGroupData.gps_points) {
            gpsPointsGroups = getGpsPointsGroup(cardsGroupData.gps_points);
        }
        properties.is_multiple = gpsPointsGroups.length > 1;
        properties.multiple_ids = properties.is_multiple ? [cardsGroupData.id] : [];
        for (const gpsPointsGroup of gpsPointsGroups) {
            let cardFeature = createCardFeature(gpsPointsGroup, cardsGroupData, properties);
            cardFeatures.push(cardFeature);
        }        
    }
    // console.log('CARD FEATURES: ', cardFeatures);
    cardFeatures = groupFeatures(cardFeatures);
    return cardFeatures;
}

function mergeFeaturesGroup(featuresGroup) {
    let mergedFeature = JSON.parse(JSON.stringify(featuresGroup[0]));
    let cardsGroupIds = [];
    let multipleIds = [];
    for (const feature of featuresGroup) {
        if (feature.properties.is_multiple) {
            mergedFeature.properties.is_multiple = true;
            multipleIds.push(feature.properties.multiple_ids[0]);
        } else if (feature.id !== mergedFeature.id) {
            mergedFeature.id = feature.id;
        }
        cardsGroupIds.push(feature.properties.cards_group_ids[0]);
    }
    mergedFeature.properties.cards_group_ids = cardsGroupIds;
    mergedFeature.properties.multiple_ids = multipleIds;
    return mergedFeature
}

export function createCardsFeaturesV2(cardsGroupsData, projectData, stageId, levelId) {
    let cardFeatures = [];
    let groupedCardsGroupsByFiguregroup = groupCardsGroupsByFiguregroup(cardsGroupsData);
    let levelData = getElementData(projectData.levels, levelId);
    let levelName = levelData ? levelData.attributes.name : '';
    let allFiguregroupIds = Object.keys(groupedCardsGroupsByFiguregroup);
    for (const figureId in projectData.figures) {
        let figureData = projectData.figures[figureId];
        let figuregroupIds = Object.keys(figureData.figuregroups);
        let filteredFiguregroupIds = allFiguregroupIds.filter((figuregroupId) => {
            return figuregroupIds.indexOf(figuregroupId) !== -1
        });
        if (!filteredFiguregroupIds.length) continue;
        let groupedCardsGroupsByCategory = groupCardsGroupsByCategory(groupedCardsGroupsByFiguregroup, filteredFiguregroupIds);
        for (const groupedCardsGroups of Object.values(groupedCardsGroupsByCategory)) {
            filteredFiguregroupIds = Object.keys(groupedCardsGroups);
            let referenceCardsGroup = groupedCardsGroups[filteredFiguregroupIds[0]][0];
            let cardCategoryData = getElementData(projectData.card_categories, referenceCardsGroup.attributes.card_category_id);
            let customcardData = getElementData(projectData.customcards, referenceCardsGroup.attributes.customcard_id);
            let appartmentData = getElementData(projectData.appartments, referenceCardsGroup.attributes.appartment_id);
            let groupedCardsGroupIds = getFeatureCardsGroupIds(groupedCardsGroupsByFiguregroup, filteredFiguregroupIds);
            let cardsGroupIds = Object.values(groupedCardsGroupIds).flat();
            let properties = {
                ...referenceCardsGroup.attributes,
                ...figureData.attributes,
                stage_id: stageId,
                color: cardCategoryData ? cardCategoryData.attributes.color : 'black',
                custom_level: customcardData ? customcardData.attributes.level_id : _('Piso'),
                custom_category: customcardData ? customcardData.attributes.card_category_id : _('Categoría'),
                custom_appartment: customcardData ? customcardData.attributes.appartment_id : '',
                custom_start: customcardData ? customcardData.attributes.start : '',
                custom_end: customcardData ? customcardData.attributes.end : '',
                appartment_name: appartmentData ? appartmentData.attributes.name : '',
                category_name: cardCategoryData ? cardCategoryData.attributes.name : '',
                level_name: levelName,
                figuregroup_ids: `,${filteredFiguregroupIds.join(',')},`,
                cards_group_ids: cardsGroupIds,
                cards_group_ids_str: `,${cardsGroupIds.join(',')},`,
                grouped_cards_group_ids: groupedCardsGroupIds,
                height: getFeatureHeight('height', levelData, cardCategoryData, figureData),
                base_height: getFeatureHeight('base_height', levelData, cardCategoryData, figureData),
            };
            properties.bim_element = getBimElement(properties.bim_description);
            properties.real_height = Math.abs(properties.height - properties.base_height);
            cardFeatures.push(
                createCardFeatureV2(figureData, properties, levelId, cardCategoryData)
            );
        }
    }
    return cardFeatures;
}

function groupCardsGroupsByFiguregroup(cardsGroupsData) {
    let groupedCardsGroupsData = {};
    for (const cardsGroupId in cardsGroupsData) {
        let cardsGroupData = cardsGroupsData[cardsGroupId];
        if (groupedCardsGroupsData.hasOwnProperty(cardsGroupData.attributes.figuregroup_id)) {
            groupedCardsGroupsData[cardsGroupData.attributes.figuregroup_id].push(
                cardsGroupData
            );
        } else {
            groupedCardsGroupsData[cardsGroupData.attributes.figuregroup_id] = [
                cardsGroupData
            ];
        }
    }
    return groupedCardsGroupsData;
}

function groupCardsGroupsByCategory(groupedCardsGroupsData, figuregroupIds) {
    let groupedCardsGroupsByCategory = new DefaultDict(Object);
    for (const figuregroupId of figuregroupIds) {
        for (const cardsGroupData of groupedCardsGroupsData[figuregroupId]) {
            let cardCategoryId = cardsGroupData.attributes.card_category_id;
            if (groupedCardsGroupsByCategory[cardCategoryId].hasOwnProperty(figuregroupId)) {
                groupedCardsGroupsByCategory[cardCategoryId][figuregroupId].push(cardsGroupData);
            } else {
                groupedCardsGroupsByCategory[cardCategoryId][figuregroupId] = [cardsGroupData];
            }
        }
    }
    return groupedCardsGroupsByCategory;
}

function getFeatureCardsGroupIds(groupedCardsGroupsData, figuregroupIds) {
    let groupedCardsGroupIds = {};
    for (const figuregroupId of figuregroupIds) {
        groupedCardsGroupIds[figuregroupId] = [];
        for (const cardsGroupData of groupedCardsGroupsData[figuregroupId]) {
            groupedCardsGroupIds[figuregroupId].push(cardsGroupData.id);
        }
    }
    return groupedCardsGroupIds;
}

function createCardFeatureV2(figureData, properties, levelId, cardCategoryData) {
    let coordinates = getGeometryCoordinates(figureData);
    let geometryType = getGeometryType(coordinates);
    let geojsonCoordinates = prepareCoordinates(coordinates, geometryType);
    let featureIdStr = `${levelId}${figureData.id}`;
    if (cardCategoryData) {
        featureIdStr += `${cardCategoryData.id}`;
    }
    let featureId = parseInt(featureIdStr);
    return {
        id: featureId,
        type: 'Feature',
        geometry: {
            type: geometryType,
            coordinates: geojsonCoordinates
        },
        properties: properties
    }
}

function getGeometryCoordinates(figureData) {
    let coordinates = [];
    for (const gpsPointId in figureData.gps_points) {
        const gpsPointData = figureData.gps_points[gpsPointId];
        coordinates.push([
            gpsPointData.attributes.longitude,
            gpsPointData.attributes.latitude
        ])
    }
    return coordinates;
}